// ---------------------------------------------------------------------------
// Trend line — ordinary-least-squares linear regression
//
// Pure math extracted from TargetChart so the overlay's slope/intercept has a
// unit-test home independent of the Chart.js render path.
// ---------------------------------------------------------------------------

/**
 * Ordinary-least-squares fit of `y = slope·x + intercept` over the data, where
 * `x` is the point's index in `data`. `null` gaps are skipped (they don't
 * contribute an observation), so the fit runs over the scored points only.
 *
 * Degenerate inputs return a flat line rather than `NaN`:
 * - fewer than two scored points → `{ slope: 0, intercept: 0 }`;
 * - all scored points share one x (impossible here since x is the index, but
 *   guarded anyway) → slope 0 with the mean as intercept.
 *
 * @param data - Per-session values, `null` for sessions without data.
 * @returns The fitted `slope` and `intercept`.
 */
export function computeLinearRegression(data: (number | null)[]): {
  slope: number;
  intercept: number;
} {
  const points: { x: number; y: number }[] = [];
  data.forEach((v, i) => {
    if (v != null) points.push({ x: i, y: v });
  });
  if (points.length < 2) return { slope: 0, intercept: 0 };

  const n = points.length;
  const sumX = points.reduce((s, p) => s + p.x, 0);
  const sumY = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0);

  const denom = n * sumX2 - sumX * sumX;
  if (denom === 0) return { slope: 0, intercept: sumY / n };

  const slope = (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;
  return { slope, intercept };
}

/**
 * Projects the regression line onto the data's x-positions, preserving `null`
 * gaps so the trend dataset aligns 1:1 with the source series (a gap in the
 * data stays a gap in the trend line).
 *
 * @param data - Per-session values, `null` for sessions without data.
 * @returns The fitted line value per index, `null` where the source is `null`.
 */
export function buildTrendData(data: (number | null)[]): (number | null)[] {
  const { slope, intercept } = computeLinearRegression(data);
  return data.map((v, i) => (v != null ? slope * i + intercept : null));
}
